import { TFunction } from 'i18next';
import CompanyManagementApi from '../../API/company-management/CompanyManagement.api';
import { UpdateCompanyRequestDto } from '../../API/company-management/dto/request/update-company.request.dto';
import { UpdateUserStatusRequestDto } from '../../API/company-management/dto/request/update-user-status.request.dto';
import UserManagementApi from '../../API/user-management/UserManagement.api';
import { AUTH_NAMESPACE } from '../Auth/authReducer';
import { startLoaderThunk, stopLoaderThunk } from '../modal/modalActions';
import {
  COMPANY_MANAGEMENT_EDIT_COMPANY,
  COMPANY_MANAGEMENT_SET_COMPANIES,
  COMPANY_MANAGEMENT_SET_COMPANY_TYPES,
  COMPANY_MANAGEMENT_SET_COMPANY_USERS,
  COMPANY_MANAGEMENT_SET_MY_COMPANY,
  COMPANY_MANAGEMENT_SET_USERS,
  companyManagementEditCompanyActionType,
  companyManagementSetCompaniesActionType,
  companyManagementSetCompanyTypesActionType,
  companyManagementSetCompanyUsersActionType,
  companyManagementSetMyCompanyActionType,
  companyManagementSetUsersActionType,
  CompanyManagementThunk,
} from './companyManagementActionTypes';
import {
  companyManagementAddNewUserDto,
  companyManagementUpdateUserDto,
} from './companyManagementDto';
import { COMPANY_MANAGEMENT_NAMESPACE } from './companyManagementReducer';
import {
  CompanyManagementCompany,
  CompanyManagementType,
  CompanyManagementUsers,
  CompanyManagementUsersPermission,
} from './companyManagementTypes';
import { UserPermissionEnum } from './enums/user-permission.enum';

export function companyManagementSetCompaniesAction(
  companies: CompanyManagementCompany[]
): companyManagementSetCompaniesActionType {
  return {
    type: COMPANY_MANAGEMENT_SET_COMPANIES,
    payload: companies,
  };
}

export function companyManagementSetCompanyTypesAction(
  companyTypes: CompanyManagementType[]
): companyManagementSetCompanyTypesActionType {
  return {
    type: COMPANY_MANAGEMENT_SET_COMPANY_TYPES,
    payload: companyTypes,
  };
}

export function companyManagementSetCompanyUsersAction(
  companyUsers: CompanyManagementUsersPermission[]
): companyManagementSetCompanyUsersActionType {
  return {
    type: COMPANY_MANAGEMENT_SET_COMPANY_USERS,
    payload: companyUsers,
  };
}

export function companyManagementEditCompanyAction(
  id: number,
  company: CompanyManagementCompany
): companyManagementEditCompanyActionType {
  return {
    type: COMPANY_MANAGEMENT_EDIT_COMPANY,
    payload: { id, company },
  };
}

export function companyManagementSetUsersAction(
  users: CompanyManagementUsers[]
): companyManagementSetUsersActionType {
  return {
    type: COMPANY_MANAGEMENT_SET_USERS,
    payload: users,
  };
}

export function companyManagementSetMyCompanyAction(
  company: CompanyManagementCompany
): companyManagementSetMyCompanyActionType {
  return {
    type: COMPANY_MANAGEMENT_SET_MY_COMPANY,
    payload: company,
  };
}

export function companyManagementFetchCompaniesThunk(
  scope?: number
): CompanyManagementThunk<Promise<void>> {
  return async (dispatch, getState) => {
    dispatch(startLoaderThunk()).finally();
    CompanyManagementApi.getCompanies(scope)
      .then((response) => {
        dispatch(companyManagementSetCompaniesAction(response.data));
      })
      .finally(() => dispatch(stopLoaderThunk()));
  };
}

export function CompanyManagementFetchMyCompanyThunk(): CompanyManagementThunk<
  Promise<void>
> {
  return async (dispatch, getState) => {
    dispatch(startLoaderThunk()).finally();
    CompanyManagementApi.getMyCompany()
      .then((response) => {
        dispatch(companyManagementSetMyCompanyAction(response.data));
      })
      .finally(() => {
        dispatch(stopLoaderThunk());
      });
  };
}

export function companyManagementGenerateInvitationLinkThunk(): CompanyManagementThunk<
  Promise<string>
> {
  return async (dispatch) => {
    await dispatch(startLoaderThunk());
    try {
      const response = await CompanyManagementApi.generateInvitationLink();
      return response.data;
    } finally {
      await dispatch(stopLoaderThunk());
    }
  };
}

export function companyManagementEditCompanyThunk(
  id: number,
  company: CompanyManagementCompany,
  callBack: (isSuccessful: boolean, s: string) => void,
  t: TFunction
): CompanyManagementThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const profile = getState()[AUTH_NAMESPACE];

    dispatch(startLoaderThunk()).finally();
    const updatedCompany: UpdateCompanyRequestDto = {
      ...company,
      token: company.token,
      name: company.name,
      website: company.website,
      industry: company.industry,
      logo: company.logo,
      expertise: company.expertise,
      social_media: company.socialMedia,
      payment_details: company.paymentDetails,
    };
    return CompanyManagementApi.updateCompany(updatedCompany)
      .then((response) => {
        dispatch(CompanyManagementFetchMyCompanyThunk());
        callBack(true, t('COMPANY_UPDATE_SUCCESSFULLY'));
      })
      .catch((error) => {
        callBack(false, error?.response?.data?.error?.message);
      })
      .finally(() => {
        dispatch(stopLoaderThunk());
      });
  };
}

export function companyManagementUploadImageThunk(
  data: any,
  callback: any
): CompanyManagementThunk<Promise<void>> {
  return async (dispatch, getState) => {
    return CompanyManagementApi.uploadCompanyImage(data)
      .then(async (response) => {
        callback(response.data.imageUrl, null);
      })
      .catch(() => {
        callback(null, 'Something went wrong...');
      });
  };
}

export function companyManagementFetchUsersPermissionsThunk(
  companyId: number
): CompanyManagementThunk<Promise<void>> {
  return async (dispatch, getState) => {
    dispatch(startLoaderThunk()).finally();
    CompanyManagementApi.getAllCompanyUsersPermissions()
      .then((response) => {
        dispatch(companyManagementSetCompanyUsersAction(response.data));
      })
      .finally(() => dispatch(stopLoaderThunk()));
  };
}

export function companyManagementUpdateUserStatusThunk(
  userId: number,
  userStatus: UpdateUserStatusRequestDto,
  companyId: number
): CompanyManagementThunk<Promise<void>> {
  return async (dispatch, getState) => {
    dispatch(startLoaderThunk()).finally();
    CompanyManagementApi.updateUserStatus(userId, userStatus)
      .then((response) => {
        dispatch(companyManagementFetchUsersPermissionsThunk(companyId));
      })
      .finally(() => dispatch(stopLoaderThunk()));
  };
}

export function companyManagementUpdateUserPermissionThunk(
  userId: number,
  permission: UserPermissionEnum,
  permissionValue: boolean
): CompanyManagementThunk<Promise<void>> {
  return async (dispatch, getState) => {
    try {
      const data = {
        [permission]: permissionValue,
      };

      dispatch(startLoaderThunk()).finally();
      await CompanyManagementApi.updateUserPermission(userId, data);
      const response = await UserManagementApi.getUserById(userId);
      const user = response.data;
      const { companyUsers } = getState()[COMPANY_MANAGEMENT_NAMESPACE];

      const newUsers: CompanyManagementUsersPermission[] = [];
      for (const oldUser of companyUsers) {
        if (oldUser.id === userId) {
          newUsers.push(user);
        } else {
          newUsers.push(oldUser);
        }
      }

      dispatch(companyManagementSetCompanyUsersAction(newUsers));
    } finally {
      dispatch(stopLoaderThunk()).finally();
    }
  };
}

export function companyManagementFetchUsersThunk(): CompanyManagementThunk<
  Promise<void>
> {
  return async (dispatch, getState) => {
    dispatch(startLoaderThunk()).finally();
    CompanyManagementApi.getCompanyUsers()
      .then((response) => {
        dispatch(companyManagementSetUsersAction(response.data));
      })
      .finally(() => dispatch(stopLoaderThunk()));
  };
}

export function UserManagementAddManyUsersFromFileThunk(
  data: any
): CompanyManagementThunk<Promise<boolean>> {
  return async (dispatch, getState) => {
    dispatch(startLoaderThunk()).finally();
    const profile = getState()['AUTH_NAMESPACE'].profile;

    try {
      const response = await UserManagementApi.addManyUsersFromFile(data);
      await dispatch(
        companyManagementFetchUsersPermissionsThunk(profile.companyId!)
      );
      return response.data;
    } finally {
      await dispatch(stopLoaderThunk());
    }
  };
}

export function UserManagementRemoveUserThunk(
  id: number
): CompanyManagementThunk<Promise<string | {}>> {
  return async (dispatch, getState) => {
    dispatch(startLoaderThunk()).finally();

    return UserManagementApi.removeUser(id).finally(() => {
      dispatch(stopLoaderThunk());
    });
  };
}

export function UserManagementEditUserThunk(
  id: number,
  user: CompanyManagementUsersPermission
): CompanyManagementThunk<Promise<string | {}>> {
  return async (dispatch, getState) => {
    dispatch(startLoaderThunk()).finally();
    const updatedUser = companyManagementUpdateUserDto(user);

    return UserManagementApi.updateUser(id, updatedUser).finally(() => {
      dispatch(stopLoaderThunk());
    });
  };
}

export function UserManagementAddUserThunk(
  user: CompanyManagementUsersPermission
): CompanyManagementThunk<Promise<void>> {
  return async (dispatch, getState) => {
    dispatch(startLoaderThunk()).finally();

    const addNewUser = companyManagementAddNewUserDto(user);

    return UserManagementApi.addUser(addNewUser)
      .then((response) => {
        dispatch(companyManagementFetchUsersThunk());
      })
      .finally(() => {
        dispatch(stopLoaderThunk());
      });
  };
}
